<template>
  <div class="manual-call-import-modal">
    <BModal
      :modal-name="modalName"
      width="520px"
      height="auto"
      :delegate="modalDelegate"
      @close="handleClose"
    >
      <BModalHeader
        :title="$t('vaApp.callImport.modal.title')"
        :needs-close-btn="!isLoading"
        @modal-close="handleClose"
      />
      <form
        class="manual-call-import-form"
        @submit.prevent="handleSubmit"
      >
        <BModalBody v-loading="isLoading">
          <div class="description">
            {{ $t('vaApp.callImport.modal.description') }}
          </div>

          <div class="form-group">
            <label
              class="form-label"
              for="call-id"
            >
              {{ $t('vaApp.callImport.modal.inputLabel') }}
            </label>
            <BInput
              v-model="callId"
              v-model:valid="isValidRef"
              input-id="call-id"
              :placeholder="$t('vaApp.callImport.modal.placeholder')"
              :rules="[requiredRule]"
              :disabled="isLoading"
              clearable
            />
          </div>
        </BModalBody>
        <BModalFooter class="modal-footer">
          <BBtn
            type="secondary"
            btn-type="button"
            :disabled="isLoading"
            @click="handleClose"
          >
            {{ $t('general.cancel.text') }}
          </BBtn>
          <BBtn
            type="primary"
            btn-type="submit"
            :disabled="!isValid || isLoading"
          >
            {{ $t('vaApp.callImport.modal.submit') }}
          </BBtn>
        </BModalFooter>
      </form>
    </BModal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useInputValidations } from '@/composable/input-validations';

type TProps = {
  modalName: string;
  isLoading?: boolean;
};

const props = withDefaults(defineProps<TProps>(), {
  isLoading: false,
});

const emit = defineEmits<{
  submit: [callId: string];
  close: [];
}>();

const { requiredRule } = useInputValidations();

const callId = ref<string>('');
const isValidRef = ref<boolean>();

const isValid = computed(() => {
  return isValidRef.value === true && callId.value.trim().length > 0;
});

const modalDelegate = computed(() => ({
  shouldClose: () => !props.isLoading,
}));

const handleClose = () => {
  if (props.isLoading) return;
  resetForm();
  emit('close');
};

const handleSubmit = () => {
  const trimmedCallId = callId.value.trim();
  emit('submit', trimmedCallId);
};

const resetForm = () => {
  callId.value = '';
  isValidRef.value = undefined;
};
</script>

<style lang="scss" scoped>
.manual-call-import-modal {

  .description {
    color: $concrete-dark;
    font-size: $fontsize-200;
    padding: $basespace-200;
    margin-bottom: $basespace-600;
    line-height: 1.6;
    background-color: $bgcolor-base;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: $basespace-200;
  }

  .form-label {
    color: $concrete-dark;
    font-size: $fontsize-100;
  }

  :deep(.modal-footer) {
    display: flex;
    justify-content: center;
    gap: $basespace-300;
  }
}
</style>
